import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Autocomplete, TextField, createFilterOptions, FormControl, InputLabel } from '@mui/material';

const filter = createFilterOptions();

const CascadingDropdown = ({ data, name, value, onChange }) => {
  const [selectedValues, setSelectedValues] = useState({});

  // Effect to parse the value prop and prefill the dropdowns
  useEffect(() => {
    if (value) {
      const valuesArray = value.split(',').map((v) => v.trim());
      const prefilledValues = {};
      valuesArray.forEach((val, index) => {
        prefilledValues[`level${index + 1}`] = val; // Set level1, level2, etc.
      });
      setSelectedValues(prefilledValues);
    }
  }, [value]);

  const handleSelectChange = (level, newValue) => {
    setSelectedValues((prev) => {
      const updatedValues = { ...prev, [level]: newValue };

      const concatenatedValue = Object.values(updatedValues).filter(Boolean).join(', ');

      onChange({ target: { name, value: concatenatedValue } });

      return updatedValues;
    });
  };

  const getOptionsForLevel = (options, selectedValue) => {
    return options.find((option) => option.label === selectedValue)?.options || [];
  };

  const renderAutocomplete = (options, level, label, allowFreeField = false) => (
    <FormControl fullWidth margin="normal" variant="standard">
      <InputLabel shrink>{label}</InputLabel>
      <Autocomplete
        value={selectedValues[level] || ''}
        onChange={(event, newValue) => {
          if (typeof newValue === 'string') {
            handleSelectChange(level, newValue); // Use string directly
          } else if (newValue && newValue.inputValue) {
            handleSelectChange(level, newValue.inputValue); // Free input field case
          } else {
            handleSelectChange(level, newValue?.label || '');
          }
        }}
        filterOptions={(options, params) => {
          const filtered = filter(options, params);
          const { inputValue } = params;

          if (allowFreeField && inputValue !== '' && !options.some((option) => inputValue === option.label)) {
            filtered.push(inputValue); // Just push the inputValue as a string
          }

          return filtered;
        }}
        selectOnFocus
        clearOnBlur
        handleHomeEndKeys
        options={options.map((option) => option.label)}
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
        renderOption={(props, option) => <li {...props}>{option}</li>}
        freeSolo={allowFreeField}
        renderInput={(params) => (
          <TextField {...params} variant="standard" margin="normal" InputLabelProps={{ shrink: true }} />
        )}
      />
    </FormControl>
  );

  const renderNestedAutocomplete = (options, level, label, allowFreeField = false) => {
    const selectedOption = selectedValues[level];
    const nestedOptions = getOptionsForLevel(options, selectedOption);

    return (
      <div>
        {renderAutocomplete(options, level, label, allowFreeField)}

        {/* Render nested autocomplete only if the previous option is selected and options exist */}
        {nestedOptions.length > 0 && selectedOption && (
          <div>
            {renderNestedAutocomplete(
              nestedOptions,
              `level${parseInt(level.replace('level', ''), 10) + 1}`, // Increment the level dynamically
              options.find((o) => o.label === selectedOption)?.label,
              options.find((o) => o.label === selectedOption)?.allowFreeField
            )}
          </div>
        )}
      </div>
    );
  };

  return <div>{renderNestedAutocomplete(data.options, 'level1', data.label, data?.allowFreeField)}</div>;
};

CascadingDropdown.propTypes = {
  data: PropTypes.shape({
    label: PropTypes.string.isRequired,
    required: PropTypes.bool,
    allowFreeField: PropTypes.bool,
    options: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        required: PropTypes.bool,
        allowFreeField: PropTypes.bool,
        options: PropTypes.array,
      })
    ).isRequired,
  }).isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

export default CascadingDropdown;
